import { useCallback, useEffect, useRef, useState } from 'react';
import { saveAdminContent, saveAdminSection } from '../../content/contentApi';
import { hydrateAllContent } from '../../content/hydrateContent';
import { resolveSiteContent } from '../mergeContent';

const SECTION_KEYS = ['services', 'caseStudies', 'blogs', 'team', 'testimonials', 'media', 'servicePages', 'aboutPage', 'homePage', 'servicesPage', 'caseStudiesPage', 'contactPage'];
const DEBOUNCE_MS = 700;

function toApiPayload(content) {
  if (!content || typeof content !== 'object') return null;
  const body = {};
  for (const key of SECTION_KEYS) {
    if (content[key] !== undefined) body[key] = content[key];
  }
  body.updatedAt = new Date().toISOString();
  return body;
}

function findChangedSections(prev, next) {
  if (!prev || !next) return [];
  return SECTION_KEYS.filter((key) => JSON.stringify(prev[key]) !== JSON.stringify(next[key]));
}

/**
 * Persists admin edits to Flask CMS (content-store.json) via PATCH section or PUT full.
 */
export function useAdminContentPersist({ refresh, onError, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(false);
  const timerRef = useRef(null);
  const latestRef = useRef(null);
  const prevRef = useRef(null);
  const savingRef = useRef(false);

  const setBaseline = useCallback((content) => {
    prevRef.current = content;
    latestRef.current = content;
    setPending(false);
  }, []);

  const persistPayload = useCallback(
    async (payload, sectionHint = null) => {
      const apiBody = toApiPayload(payload);
      if (!apiBody || savingRef.current) return null;

      savingRef.current = true;
      setSaving(true);
      setPending(false);

      try {
        let updatedAt = apiBody.updatedAt;
        const changed = sectionHint
          ? [sectionHint]
          : findChangedSections(prevRef.current, payload);

        if (changed.length === 1 && SECTION_KEYS.includes(changed[0])) {
          const data = await saveAdminSection(changed[0], apiBody[changed[0]]);
          updatedAt = data.updatedAt || updatedAt;
        } else {
          const data = await saveAdminContent(apiBody);
          updatedAt = data.updatedAt || apiBody.updatedAt;
        }

        const resolved = resolveSiteContent({ ...apiBody, updatedAt });
        try {
          hydrateAllContent(resolved);
        } catch (hydrateErr) {
          console.error('[admin] hydrate after save failed:', hydrateErr);
        }
        await refresh?.(updatedAt);
        prevRef.current = payload;
        onSuccess?.('Saved to server. The live site updates within a few seconds.');
        return updatedAt;
      } catch (e) {
        const msg = e?.message || 'Failed to save to server';
        onError?.(msg, e);
        throw e;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [refresh, onError, onSuccess]
  );

  const schedulePersist = useCallback(
    (nextContent) => {
      latestRef.current = nextContent;
      setPending(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        const payload = latestRef.current;
        if (!payload) return;
        const sections = findChangedSections(prevRef.current, payload);
        const hint = sections.length === 1 ? sections[0] : null;
        persistPayload(payload, hint).catch(() => {});
      }, DEBOUNCE_MS);
    },
    [persistPayload]
  );

  const onContentChange = useCallback(
    (nextContent, setContent) => {
      setContent(nextContent);
      schedulePersist(nextContent);
    },
    [schedulePersist]
  );

  const persistNow = useCallback(
    async (content) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setPending(false);
      const payload = content ?? latestRef.current;
      if (!payload) return null;
      const sections = findChangedSections(prevRef.current, payload);
      const hint = sections.length === 1 ? sections[0] : null;
      return persistPayload(payload, hint);
    },
    [persistPayload]
  );

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return { saving, pending, onContentChange, persistNow, setBaseline };
}
